/**
 * Plan「评测配置」数据层（后台页面对齐批 3 B 路：题库快照、体验评分两页；批 4：判官提示、判分规则两页；只读）。
 * 单独成文件（不往 plan-lab.ts 里加），和并行改别的页的同事错开。
 * A-41～A-43 是现有题库接口；`/items`、`/lineage`、`/sim`、`/cx` 是本批新增（后端 api/eval_config.py）；10-26 加 `/capability-gate`（能力对齐闸）。
 * 盲测保护：`/items` 里 `sealed: true` 的题只有题号、题族、阶段、所属测试集；页面不再为它请求 A-43。
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

const P = "/admin/v1/plan-lab";

export type PlanBankRow = {
	bank_id: string;
	exported_at?: string | null;
	loop_code_version?: string | null;
	scenario_count: number;
	gold_count?: number;
	gold_version?: string | null;
};

/** A-41 题库快照列表（新 → 旧） */
export function usePlanBanks() {
	return useQuery<{ rows: PlanBankRow[] }>({
		queryKey: ["plan-lab", "bank", "list"],
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots`)).data,
	});
}

export type DemandTypeMap = {
	by_name?: Record<string, string>;
	by_tool?: Record<string, string>;
};

export type PlanBankDetail = {
	manifest: {
		bank_id?: string;
		exported_at?: string;
		loop_code_version?: string;
		gold_version?: string;
		env_fingerprint?: string;
		sim_fingerprint?: string;
		judge_fingerprint?: string;
		demand_type_map?: DemandTypeMap;
		/** 金标带诉求编号的快照才有（派生脚本 gold_stamp_demand_codes 写入） */
		gold_demand_code?: {
			field?: string;
			unsupported?: string;
			note?: string;
			/** 用户逐条裁定的金标写法 → 编号（换写时优先于归一表） */
			rulings?: Record<string, string>;
		};
		env?: {
			env_fingerprint?: string;
			seed_digest?: string;
			recordings_digest?: string;
			tool_backend?: { default?: string };
			note?: string;
		};
		models?: Record<string, string>;
		[k: string]: unknown;
	};
	test_sets: { name: string; desc?: string | null; n: number; ids: string[] }[];
};

/** A-42 一份题库快照（只取 manifest 与测试集；题目行用 `/items`） */
export function usePlanBankDetail(bankId: string | null) {
	return useQuery<PlanBankDetail>({
		queryKey: ["plan-lab", "bank", "detail", bankId],
		enabled: !!bankId,
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots/${bankId}`)).data,
	});
}

export type PlanGoldItem = {
	item_id: string;
	demand?: string | null;
	/** 诉求编号（标签体系 v2 T-6）：意图 `intent.<一级>.<二级>`，能力外 `eval.unsupported`；历史快照没有 */
	demand_code?: string | null;
	/** 判分配对用的诉求（叶子键）：有编号取编号，没有按本快照诉求映射归一 */
	demand_norm?: string | null;
	/** 能力外事项的提及意图（编号列表）；没有该字段的按原文解析 */
	mention_codes?: string[] | null;
	object?: string;
	outcome?: string | null;
	alt_outcomes?: string[];
	/** 替代结局的前提（如先说清退不到原卡、客户接受后再提交） */
	alt_premise?: string | null;
	/** 越权 ／ 查无题的显式标记 */
	flags?: string[];
	tools?: string[];
	requirements?: string[];
	forbid?: string[];
	source?: string | null;
};

export type PlanBankItem = {
	scenario_id: string;
	family: string;
	stage?: string | null;
	test_sets: string[];
	/** 盲测 ／ 留出题：只有上面四项 */
	sealed: boolean;
	intent?: string | null;
	difficulty?: string | null;
	desc?: string | null;
	has_gold?: boolean;
	gold_status?: "ready" | "pending_solvable" | "needs_fix" | null;
	required_trials?: number;
	assertions?: string[];
	gold_items?: PlanGoldItem[];
};

/** 退役集（判分规则 EV-26）：集名 → 退役日期、原因、替代集 */
export type PlanRetiredSets = Record<
	string,
	{ retired_at?: string; reason?: string; replaced_by?: string | null }
>;

export function usePlanBankItems(bankId: string | null) {
	return useQuery<{
		bank_id: string;
		sealed_count: number;
		retired_sets?: PlanRetiredSets;
		rows: PlanBankItem[];
	}>({
		queryKey: ["plan-lab", "bank", "items", bankId],
		enabled: !!bankId,
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots/${bankId}/items`)).data,
	});
}

export type PlanBankLineageStep = {
	bank_id: string;
	exported_at?: string | null;
	derived_from?: string | null;
	note?: string | null;
	changes?: Record<string, number>;
	prompts_changed?: string[];
	scenario_count?: number;
	gold_count?: number;
	missing?: boolean;
};

export function usePlanBankLineage(bankId: string | null) {
	return useQuery<{ bank_id: string; chain: PlanBankLineageStep[] }>({
		queryKey: ["plan-lab", "bank", "lineage", bankId],
		enabled: !!bankId,
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots/${bankId}/lineage`)).data,
	});
}

export type PlanBankScenario = {
	scenario: {
		id?: string;
		desc?: string;
		persona?: string | null;
		customer_id?: string;
		data_refs?: { orders?: string[]; products?: string[] };
		reason_for_call?: string;
		known_info?: string;
		unknown_info?: string;
		task_instructions?: string;
		eval?: {
			expect_skills?: string[];
			forbid_skills?: string[];
			communicate_info?: string[];
			nl_assertions?: string[];
		};
	};
	meta: { fingerprint?: string; required_trials?: number };
};

/** A-43 一道题（只给不是盲测的题调用） */
export function usePlanBankScenario(bankId: string | null, scenarioId: string | null) {
	return useQuery<PlanBankScenario>({
		queryKey: ["plan-lab", "bank", "scenario", bankId, scenarioId],
		enabled: !!bankId && !!scenarioId,
		queryFn: async () =>
			(await apiClient.get(`${P}/bank/snapshots/${bankId}/scenarios/${scenarioId}`)).data,
	});
}

export type PlanBankSim = {
	bank_id: string;
	model?: string | null;
	prompt_keys: string[];
	prompts: Record<string, string | null>;
	fingerprint?: string | null;
	personas: { name: string; weight?: number | null; desc: string }[];
	max_turns: { default: number; by_family: Record<string, number> };
	trials: { multi: number; single: number };
	/** 串话重采上限（EV-22）；老后端没有 */
	role_marker_resample?: number;
};

export function usePlanBankSim(bankId: string | null) {
	return useQuery<PlanBankSim>({
		queryKey: ["plan-lab", "bank", "sim", bankId],
		enabled: !!bankId,
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots/${bankId}/sim`)).data,
	});
}

/** 能力对齐闸（10-26）：按当前能力清单对这份快照逐题核；留出题明细只有题号、集、检查编号与级别（sealed） */
export type PlanGateLevel = "block" | "warn" | "info";
export type PlanGateRow = {
	scenario_id: string;
	set: string;
	check: string;
	level: PlanGateLevel;
	sealed: boolean;
	item?: string | null;
	msg?: string;
	evidence?: Record<string, unknown>;
};
export type PlanGateCheck = { id: string; name: string; level: string; how: string };
export type PlanBankGate = {
	bank_id: string;
	profile: string;
	as_of: string;
	capability_digest: string;
	scenarios: number;
	block: number;
	warn: number;
	info: number;
	blocked_scenarios: string[];
	by_set: { name: string; scenarios: number; block: number; warn: number; info: number }[];
	/** 「C01·block」→ 条数 */
	by_check: Record<string, number>;
	checks: PlanGateCheck[];
	c16: { item: string; status: string; where: string }[];
	skipped_sets: string[];
	/** 退役集（判分规则 EV-26）：闸不查 */
	retired_sets?: ({ name: string } & {
		retired_at?: string;
		reason?: string;
		replaced_by?: string | null;
	})[];
	rows: PlanGateRow[];
	spec: { path: string; text: string | null };
};

export function usePlanBankGate(bankId: string | null) {
	return useQuery<PlanBankGate>({
		queryKey: ["plan-lab", "bank", "capability-gate", bankId],
		enabled: !!bankId,
		queryFn: async () =>
			(await apiClient.get(`${P}/bank/snapshots/${bankId}/capability-gate`)).data,
	});
}

export type PlanBankCx = {
	bank_id: string;
	enabled: boolean;
	counts_in_pass_rate: boolean;
	model?: string | null;
	prompt_keys: string[];
	prompts: Record<string, string | null>;
	/** desc：中文说法（英文简写已换成理由中文名）；raw：提示词原文 */
	anchors: { score: number; desc: string; raw?: string }[];
	reasons: { key: string; label: string; negative_when: string; values: string[] }[];
	score_cap: { reason: string; when: string; cap: number };
	review_rules: { id: string; desc: string }[];
	report_metrics: { name: string; desc: string }[];
	experience: { similarity: number; signals: { name: string; desc: string }[] };
};

export function usePlanBankCx(bankId: string | null) {
	return useQuery<PlanBankCx>({
		queryKey: ["plan-lab", "bank", "cx", bankId],
		enabled: !!bankId,
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots/${bankId}/cx`)).data,
	});
}

/** 批 4：判官提示页（`/judges`，后端 api/eval_config.py · bank_judges） */
export type PlanJudgeVar = { name: string; desc: string; source?: string };

export type PlanJudge = {
	key: string;
	name: string;
	judges_what: string;
	/** 一票否决 ／ 观测列 ／ 诊断 */
	verdict: string;
	verdict_note: string;
	/** 判分规则注册表编号（EV-xx） */
	rule_ids: string[];
	when: string;
	prompt_key: string;
	/** 复用哪个判官的提示词（指南、编造复用 nl） */
	reuses?: string;
	/** 有专页的判官（CX → eval-cx） */
	link?: string;
	fixed_assertions?: string[];
	inputs: string[];
	not_given: string[];
	output: string;
	failure: string;
	model?: string | null;
	enabled: boolean;
	prompt_keys: string[];
	prompts: Record<string, string | null>;
	variables: Record<string, PlanJudgeVar[]>;
};

export type PlanBankJudges = {
	bank_id: string;
	model?: string | null;
	fingerprint?: string | null;
	temperature: number;
	judges: PlanJudge[];
};

export function usePlanBankJudges(bankId: string | null) {
	return useQuery<PlanBankJudges>({
		queryKey: ["plan-lab", "bank", "judges", bankId],
		enabled: !!bankId,
		queryFn: async () => (await apiClient.get(`${P}/bank/snapshots/${bankId}/judges`)).data,
	});
}

/** 批 4：判分规则注册表（`/eval-config/scoring-rules`，后端 eval/scoring_rules.py） */
export type PlanScoringRule = {
	id: string;
	kind: string;
	name: string;
	does: string;
	value: string;
	where: string;
};

export function usePlanScoringRules() {
	return useQuery<{ kinds: string[]; rules: PlanScoringRule[] }>({
		queryKey: ["plan-lab", "eval-config", "scoring-rules"],
		queryFn: async () => (await apiClient.get(`${P}/eval-config/scoring-rules`)).data,
	});
}
